<template>
  <Label class="radio-button">
    <input
      type="radio"
      :checked="isChecked"
      :name="name"
      :value="value"
      :disabled="disabled"
      @change="onChange"
      @keydown.space.prevent="onChange($event)"
      @keydown.enter.prevent="onChange($event)"
      tabindex="0"
    />
    <slot />
  </Label>
</template>

<script setup lang="ts">
import { computed } from 'vue';

import Label from './label.vue';

interface RadioButtonProps {
  name: string;
  value: any;
  modelValue: any;
  disabled?: boolean;
}

const props = defineProps<RadioButtonProps>();
const emit = defineEmits(['update:modelValue']);
const isChecked = computed(() => props.value === props.modelValue);

function onChange(event: Event) {
  const newValue = (event.target as HTMLInputElement).checked ? props.value : null;

  emit('update:modelValue', newValue);
}
</script>

<style scoped lang="scss">
.radio-button {
  display: inline-flex;
  align-items: center;
  padding: var(--t-spacing-md);
  gap: var(--t-spacing-md);

  border: 1px solid var(--t-color-border-secondary);
  border-radius: var(--t-border-radius-sm);

  &:has(input:checked) {
    background: var(--t-color-neutral-100);
  }

  &:has(input:disabled) {
    opacity: 0.5;
  }

  &:has(input:focus-visible) {
    border-color: var(--t-color-neutral-950);
    box-shadow: var(--t-form-field-focus-shadow);
  }

  &:hover:has(:not(input:disabled)) {
    background: var(--t-color-neutral-200);
    cursor: pointer;

    input {
      background-color: var(--t-color-neutral-white);
    }
  }
}

input {
  appearance: none;
  background-color: transparent;
  outline: none;
  border-radius: var(--t-border-radius-half-rounded);
  border: 1px solid var(--t-color-border-secondary);
  width: var(--t-spacing-lg);
  height: var(--t-spacing-lg);
  min-width: var(--t-spacing-lg);
  min-height: var(--t-spacing-lg);
  flex-shrink: 0;
  padding: 0;
  margin: 0;
  cursor: pointer;

  &:focus-visible {
    outline: 2px solid var(--t-color-neutral-950);
    outline-offset: 2px;
  }

  &:checked {
    background-color: var(--t-color-neutral-white);
    border-color: var(--t-color-neutral-950);
    border-width: calc(var(--t-spacing-lg) / 4);
  }
}
</style>
